/**
 * Client App — Directus Data Layer
 *
 * Uses real Directus collections:
 * - User data from /users/me (no separate contacts collection needed)
 * - Listings (where seller = userId) for seller-side deals
 * - Purchases (where buyer = userId) for buyer-side deals
 * - deal_tasks (linked to user's listings/purchases)
 * - communication_logs (where lead relates to user)
 * - Files via /files endpoints
 */

const DIRECTUS_URL = import.meta.env.VITE_DIRECTUS_URL ?? 'https://app.luxepropertyrescue.com';

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function directusFetch<T>(url: string, token: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${DIRECTUS_URL}${url}`, {
    ...options,
    headers: {
      ...authHeaders(token),
      ...(options?.headers ?? {}),
    },
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.errors?.[0]?.message ?? `API error: ${res.status}`);
  }

  return data;
}

// ─────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────

export async function loginWithCredentials(email: string, password: string) {
  const res = await fetch(`${DIRECTUS_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.errors?.[0]?.message ?? 'Invalid credentials');
  }

  return data.data as { access_token: string; refresh_token: string };
}

// ─────────────────────────────────────────────────────────────
// User
// ─────────────────────────────────────────────────────────────

export async function getCurrentUser(token: string) {
  return directusFetch<{ data: DirectusUser }>('/users/me', token);
}

// ─────────────────────────────────────────────────────────────
// Listings (seller-side deals)
// ─────────────────────────────────────────────────────────────

export async function getListingsByUser(userId: string, token: string) {
  const data = await directusFetch<{ data: ClientListing[] }>(
    `/items/listings?filter[seller][_eq]=${userId}&fields=*&sort=-date_created`,
    token
  );
  return data.data ?? [];
}

// ─────────────────────────────────────────────────────────────
// Purchases (buyer-side deals)
// ─────────────────────────────────────────────────────────────

export async function getPurchasesByUser(userId: string, token: string) {
  const data = await directusFetch<{ data: ClientPurchase[] }>(
    `/items/purchases?filter[buyer][_eq]=${userId}&fields=*&sort=-date_created`,
    token
  );
  return data.data ?? [];
}

// ─────────────────────────────────────────────────────────────
// Tasks
// ─────────────────────────────────────────────────────────────

export async function getTasksByDeals(dealIds: string[], token: string) {
  if (!dealIds.length) return [];

  // Build OR filter for listing and purchase relations
  const listingFilters = dealIds.map((id) => `filter[listing][_eq]=${id}`);
  const purchaseFilters = dealIds.map((id) => `filter[purchase][_eq]=${id}`);

  // Use _or filter for combined query
  const filterParams = [...listingFilters, ...purchaseFilters]
    .map((f, i) => f.replace('filter[', `filter[_or][${i}][`))
    .join('&');

  const data = await directusFetch<{ data: ClientTask[] }>(
    `/items/deal_tasks?${filterParams}&filter[status][_neq]=done&sort=due_date&limit=10`,
    token
  );
  return data.data ?? [];
}

// ─────────────────────────────────────────────────────────────
// Communication Logs
// ─────────────────────────────────────────────────────────────

export async function getCommunicationLogs(userId: string, token: string) {
  const data = await directusFetch<{ data: ClientCommunicationLog[] }>(
    `/items/communication_logs?filter[lead_id][_eq]=${userId}&sort=-date_created&limit=5`,
    token
  );
  return data.data ?? [];
}

// ─────────────────────────────────────────────────────────────
// Files
// ─────────────────────────────────────────────────────────────

export async function getFilesByDeals(dealIds: string[], token: string) {
  if (!dealIds.length) return [];
  // Query files endpoint — Directus files don't have deal relations by default,
  // so we fetch all files accessible to the user
  const data = await directusFetch<{ data: ClientFile[] }>(
    `/files?sort=-uploaded_on&limit=50`,
    token
  );
  return data.data ?? [];
}

export async function uploadFile(
  file: File,
  metadata: { category: string; deal_id: string },
  token: string
) {
  const formData = new FormData();
  formData.append('file', file);

  const uploadRes = await fetch(`${DIRECTUS_URL}/files`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const uploadData = await uploadRes.json();
  if (!uploadRes.ok) {
    throw new Error(uploadData.errors?.[0]?.message ?? `Upload error: ${uploadRes.status}`);
  }

  const fileId = uploadData.data.id;
  const updateRes = await fetch(`${DIRECTUS_URL}/files/${fileId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({
      title: file.name,
      category: metadata.category,
    }),
  });

  if (updateRes.ok) {
    const updateData = await updateRes.json();
    return updateData.data;
  }

  return uploadData.data;
}

export async function deleteFile(fileId: string, token: string) {
  const res = await fetch(`${DIRECTUS_URL}/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.errors?.[0]?.message ?? `Delete error: ${res.status}`);
  }

  return true;
}

export function getDownloadUrl(fileId: string) {
  return `${DIRECTUS_URL}/assets/${fileId}`;
}

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface DirectusUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
}

export interface ClientListing {
  id: string;
  property_address: string;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  status: 'preparing' | 'active' | 'under_contract' | 'closed' | 'terminated';
  listing_price: number | null;
  listing_date: string | null;
  closing_date: string | null;
  description: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  property_type: string | null;
  mls_number: string | null;
  selected_option: string | null;
  date_created: string | null;
}

export interface ClientPurchase {
  id: string;
  property_address: string;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  status: 'preparing' | 'active' | 'under_contract' | 'closed' | 'terminated';
  asking_price: number | null;
  offer_price: number | null;
  closing_date: string | null;
  description: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  property_type: string | null;
  mls_number: string | null;
  date_created: string | null;
}

/** Unified deal type for client display */
export interface ClientDeal {
  id: string;
  type: 'listing' | 'purchase';
  property_address: string;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  status: string;
  price: number | null;
  date_created: string | null;
}

export interface ClientTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  description: string | null;
}

export interface ClientCommunicationLog {
  id: string;
  type: string;
  direction: string;
  subject: string | null;
  body: string | null;
  status: string;
  date_created: string | null;
}

export interface ClientFile {
  id: string;
  title?: string;
  filename_download?: string;
  filesize?: number;
  uploaded_on?: string;
  category?: string;
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Convert listings + purchases into unified ClientDeal array */
export function toClientDeals(
  listings: ClientListing[],
  purchases: ClientPurchase[]
): ClientDeal[] {
  const fromListings: ClientDeal[] = listings.map((l) => ({
    id: l.id,
    type: 'listing',
    property_address: l.property_address,
    street_address: l.street_address,
    city: l.city,
    state: l.state,
    zip_code: l.zip_code,
    status: l.status,
    price: l.listing_price,
    date_created: l.date_created,
  }));

  const fromPurchases: ClientDeal[] = purchases.map((p) => ({
    id: p.id,
    type: 'purchase',
    property_address: p.property_address,
    street_address: p.street_address,
    city: p.city,
    state: p.state,
    zip_code: p.zip_code,
    status: p.status,
    price: p.asking_price,
    date_created: p.date_created,
  }));

  return [...fromListings, ...fromPurchases];
}
